import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { HeroUIProvider } from '@heroui/react';
import { HelmetProvider } from 'react-helmet-async';

import { TransitionProvider } from './components/TransitionProvider';
import { PreloaderContext } from './context/PreloaderContext';
import CookieConsent from './components/CookieConsent';
import { trackPageview } from './lib/analytics';
import Preloader from './components/Preloader';
import HomePage from './components/HomePage';
import AboutPage from './components/AboutPage';
import ContactPage from './components/ContactPage';
import WorksPage from './components/WorksPage';
import ServicesPage from './components/ServicesPage';
import TermsPage from './components/TermsPage';
import BrandGuidelinesPage from './components/BrandGuidelinesPage';
import AdminRestartPage from './components/AdminRestartPage';
import ProjectPage from './components/ProjectPage';
import AnimationGallery from './components/AnimationGallery';
import InsightsPage from './components/InsightsPage';
import InsightPage from './components/InsightPage';
import { useLenis } from './hooks/useLenis';

import './index.css';

function ScrollToTop() {
  const { pathname, search } = useLocation();

  useEffect(() => {
    const scrollToTop = () => {
      const lenis = typeof window !== 'undefined' ? window.__lenis : null;
      if (lenis?.scrollTo) {
        lenis.scrollTo(0, { immediate: true, lock: true });
      } else {
        window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      }
    };

    // Small delay to ensure Lenis is ready
    const timeoutId = setTimeout(scrollToTop, 0);
    return () => clearTimeout(timeoutId);
  }, [pathname, search]);

  return null;
}

function LenisScroller() {
  useLenis();
  return null;
}

function RouteScopedLoader({ onComplete }) {
  const [shown, setShown] = useState(false);

  useEffect(() => {
    // Only show preloader on first load (check sessionStorage)
    const hasLoadedBefore = sessionStorage.getItem('hasLoadedBefore');
    
    if (!hasLoadedBefore) {
      setShown(true);
    } else {
      onComplete?.();
    }
  }, [onComplete]);

  if (!shown) return null;
  return <Preloader onComplete={onComplete} />;
}

function AnalyticsTracker() {
  const { pathname, search } = useLocation();
  useEffect(() => {
    trackPageview(`${pathname}${search}`);
  }, [pathname, search]);
  return null;
}

function AppContent() {
  const location = useLocation();
  const [loaderComplete, setLoaderComplete] = useState(false);

  const handleLoaderComplete = () => {
    // Mark the intro as seen for the rest of the session, in one place.
    try {
      sessionStorage.setItem('hasLoadedBefore', 'true');
    } catch (_) {
      // sessionStorage can be unavailable (private mode / SSR) — ignore.
    }
    setLoaderComplete(true);
  };

  return (
    <HeroUIProvider>
      <PreloaderContext.Provider value={loaderComplete}>
        <TransitionProvider>
          <LenisScroller />
          <ScrollToTop />
          <AnalyticsTracker />
          {!loaderComplete && (
            <RouteScopedLoader onComplete={handleLoaderComplete} />
          )}
          <div className="App">
            <AnimatePresence mode="wait">
              <Routes location={location} key={`${location.pathname}${location.search}`}>
                <Route path="/" element={<HomePage />} />
                <Route path="/about" element={<AboutPage />} />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="/works" element={<WorksPage />} />
                <Route path="/services" element={<ServicesPage />} />
                <Route path="/terms" element={<TermsPage />} />
                <Route path="/brand-guidelines" element={<BrandGuidelinesPage />} />
                <Route path="/admin/restart-calc" element={<AdminRestartPage />} />
                <Route path="/project/:id" element={<ProjectPage />} />
                <Route path="/project" element={<ProjectPage />} />
                <Route path="/animations" element={<AnimationGallery />} />
                <Route path="/insights" element={<InsightsPage />} />
                <Route path="/insights/:slug" element={<InsightPage />} />
              </Routes>
            </AnimatePresence>
          </div>
          <CookieConsent />
        </TransitionProvider>
      </PreloaderContext.Provider>
    </HeroUIProvider>
  );
}

function App() {
  return (
    <HelmetProvider>
      <Router>
        <AppContent />
      </Router>
    </HelmetProvider>
  );
}

export default App;
